import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, CalendarCheck, Info, UserPlus } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FieldSeparator
} from '@bliks-io/ui';
import {
  CreatePartnerLeadRequestSchema,
  PartnerLeadInterestProofSchema,
  PartnerLeadLicenseTypeSchema,
  PartnerLeadReactionSchema,
  PartnerLeadSalutationSchema,
  type CreatePartnerLeadRequest,
  type PartnerLeadInterestProof,
  type PartnerLeadLicenseType,
  type PartnerLeadSalutation
} from '@bliks-io/contracts';
import { leadDefaults, type NewLeadPrefill } from './newLeadDefaults';
import { FormInput, FormSelect, FormTextarea } from '@/components/form/rhf';
import { CrudErrorAlert } from '@/components/crud';
import {
  CalendlyInline,
  type CalendlyScheduledPayload
} from '@/pages/partner/components/CalendlyInline';
import { buildPartnerLeadCalendlyUrl } from './calendly';
import { useProfile } from '@/api/queries/useProfile';
import { usePrecheckPartnerLead } from '@/pages/partner/api/mutations/usePrecheckPartnerLead';

interface NewLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreatePartnerLeadRequest) => void;
  isSubmitting?: boolean;
  /** Server-side failure of the create-lead call, shown above the wizard. */
  error?: string;
  onErrorDismiss?: () => void;
  prefill?: NewLeadPrefill;
}

type PartnerLeadReaction = NonNullable<CreatePartnerLeadRequest['positiveReaction']>;

// The option VALUES are the German strings HubSpot stores, so they come from the
// contract enums rather than being retyped here; these tables only bind each
// value to its label key. `Record<Enum, string>` is exhaustive, so a value added
// to (or renamed in) the contract fails the build instead of silently losing its
// translation or killing a branch below.
const SALUTATION_LABEL_KEYS: Record<PartnerLeadSalutation, string> = {
  Herr: 'partner.newLead.options.salutation.herr',
  Frau: 'partner.newLead.options.salutation.frau',
  Divers: 'partner.newLead.options.salutation.divers'
};

const INTEREST_PROOF_LABEL_KEYS: Record<PartnerLeadInterestProof, string> = {
  'Vorheriger persönlicher Kontakt (Telefon oder Meeting)':
    'partner.newLead.options.interestProof.personal',
  'Lead hat aktiv Informationen angefragt (Produkt oder Angebot)':
    'partner.newLead.options.interestProof.requested',
  'Follow-up nach Event oder Webinar': 'partner.newLead.options.interestProof.event'
};

const REACTION_LABEL_KEYS: Record<PartnerLeadReaction, string> = {
  Ja: 'partner.newLead.options.reaction.yes',
  Nein: 'partner.newLead.options.reaction.no'
};

const LICENSE_TYPE_LABEL_KEYS: Record<PartnerLeadLicenseType, string> = {
  'B2B-Lizenz (Für Endkunden)': 'partner.newLead.options.licenseType.b2b',
  'Partner-Lizenz (Mit Seats)': 'partner.newLead.options.licenseType.withSeats',
  'Partner-Lizenz (Ohne Seats)': 'partner.newLead.options.licenseType.withoutSeats'
};

/** Qualification gate — only a positively-reacting lead may be created. */
const REACTION_POSITIVE: PartnerLeadReaction = 'Ja';

/** Partner licences (unlike the B2B end-customer one) ask for typical industries. */
const PARTNER_LICENSE_TYPES: readonly PartnerLeadLicenseType[] = [
  'Partner-Lizenz (Mit Seats)',
  'Partner-Lizenz (Ohne Seats)'
];

/** The lead reacted, but not positively — the wizard stops here. */
function isDisqualified(reaction: PartnerLeadReaction | undefined): boolean {
  return reaction !== undefined && reaction !== REACTION_POSITIVE;
}

function wantsPartnerLicense(licenseType: PartnerLeadLicenseType | undefined): boolean {
  return licenseType !== undefined && PARTNER_LICENSE_TYPES.includes(licenseType);
}

// Step-1 fields validated before advancing; the appointment is validated on the
// final submit (the schema requires both Calendly URIs).
const STEP1_FIELDS: Path<CreatePartnerLeadRequest>[] = [
  'salutation',
  'title',
  'firstName',
  'lastName',
  'email',
  'phone',
  'company',
  'role',
  'interestProof',
  'contactDate',
  'positiveReaction',
  'licenseType',
  'typicalIndustries',
  'openQuestions',
  'comment'
];

export function NewLeadDialog({ open, onOpenChange, ...wizardProps }: NewLeadDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-full max-w-3xl overflow-y-auto">
        {/* Radix unmounts DialogContent's children while closed, so mounting
            the wizard inside it makes every open a fresh instance — no reset
            effect needed. */}
        <NewLeadWizard onOpenChange={onOpenChange} {...wizardProps} />
      </DialogContent>
    </Dialog>
  );
}

function NewLeadWizard({
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  error,
  onErrorDismiss,
  prefill
}: Omit<NewLeadDialogProps, 'open'>) {
  const { t, i18n } = useTranslation();
  const { data: me } = useProfile();
  const [step, setStep] = useState<1 | 2>(1);
  const precheck = usePrecheckPartnerLead();

  const form = useForm<CreatePartnerLeadRequest>({
    resolver: zodResolver(CreatePartnerLeadRequestSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: leadDefaults(prefill)
  });

  const toOptions = <T extends string>(values: readonly T[], labelKeys: Record<T, string>) =>
    values.map((value) => ({ value, label: t(labelKeys[value]) }));

  const goToAppointment = async () => {
    const ok = await form.trigger(STEP1_FIELDS);
    if (!ok) return;
    try {
      const { exists } = await precheck.mutateAsync(form.getValues('email'));
      if (exists) {
        // Short red error on the field itself (like other validators); the
        // friendly, solution-oriented note shows near the action below. Neither
        // reveals that the contact already exists.
        form.setError('email', {
          type: 'manual',
          message: 'partner.newLead.emailUnusable'
        });
        return;
      }
    } catch {
      // Pre-check unavailable → don't block the sales flow; the n8n intake dedupes.
    }
    setStep(2);
  };

  const handleScheduled = (payload: CalendlyScheduledPayload) => {
    const eventUri = payload.event?.uri ?? '';
    const inviteeUri = payload.invitee?.uri ?? '';
    form.setValue('appointmentEventUri', eventUri, { shouldValidate: true });
    form.setValue('appointmentInviteeUri', inviteeUri, { shouldValidate: true });
    // Booking is the trigger: submit the lead the moment the appointment is
    // booked (no separate confirm click) so a booked-but-abandoned slot can't
    // leave an orphan appointment. The Create-lead button remains as a retry.
    //
    // Both URIs are required — the backend verifies the invitee belongs to the
    // event — so an event without an invitee must NOT count as booked. The
    // `shouldValidate` above turns that case into the visible form-level error
    // below instead of an enabled button that silently does nothing.
    if (eventUri && inviteeUri) {
      void form.handleSubmit(onSubmit)();
    }
  };

  const interestProof = form.watch('interestProof');
  const contactDate = form.watch('contactDate');
  const positiveReaction = form.watch('positiveReaction');
  const licenseType = form.watch('licenseType');
  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');
  const email = form.watch('email');
  const booked = Boolean(form.watch('appointmentEventUri') && form.watch('appointmentInviteeUri'));
  // Pre-check blocked this email (manual error) → show the friendly note near the
  // action. Reading formState.errors here keeps it in sync with the field error.
  const emailBlocked = form.formState.errors.email?.type === 'manual';
  // Step 2 renders no input for either appointment URI, so a schema failure on
  // them has nowhere to attach — surface it at form level rather than leaving
  // the user with no feedback at all. The wizard owns the copy because only it
  // knows the way out is booking another slot.
  const hasAppointmentError = Boolean(
    form.formState.errors.appointmentEventUri || form.formState.errors.appointmentInviteeUri
  );

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-3">
          {step === 1 ? (
            <UserPlus className="w-5 h-5 text-muted-foreground" />
          ) : (
            <CalendarCheck className="w-5 h-5 text-muted-foreground" />
          )}
          <DialogTitle>{t('partner.newLead.title')}</DialogTitle>
          <Badge variant="outline" className="ml-auto font-normal">
            {t('partner.newLead.stepIndicator', { current: step, total: 2 })}
          </Badge>
        </div>
        <DialogDescription>
          {step === 1 ? t('partner.newLead.subtitle') : t('partner.newLead.appointmentSubtitle')}
        </DialogDescription>
      </DialogHeader>

      {error && <CrudErrorAlert error={error} onDismiss={onErrorDismiss} />}

      {/* Step 1 — lead data + qualification */}
      {step === 1 && (
        <>
          {/* Said before the form, not only on step 2: a partner who fills in
              everything and then meets the booking step for the first time
              has no way to save the contact without the call. */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{t('partner.newLead.discoveryCallNotice')}</AlertDescription>
          </Alert>
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <FieldSeparator>{t('partner.newLead.sections.contact')}</FieldSeparator>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormSelect
                  control={form.control}
                  name="salutation"
                  label={t('partner.newLead.fields.salutation')}
                  placeholder={t('partner.newLead.placeholders.select')}
                  options={toOptions(PartnerLeadSalutationSchema.options, SALUTATION_LABEL_KEYS)}
                  required
                />
                <FormInput
                  control={form.control}
                  name="title"
                  label={t('partner.newLead.fields.title')}
                />
                <FormInput
                  control={form.control}
                  name="firstName"
                  label={t('partner.newLead.fields.firstName')}
                  placeholder={t('partner.newLead.placeholders.firstName')}
                  required
                />
                <FormInput
                  control={form.control}
                  name="lastName"
                  label={t('partner.newLead.fields.lastName')}
                  placeholder={t('partner.newLead.placeholders.lastName')}
                  required
                />
                <FormInput
                  control={form.control}
                  name="email"
                  type="email"
                  label={t('partner.newLead.fields.email')}
                  placeholder={t('partner.newLead.placeholders.email')}
                  required
                />
                <FormInput
                  control={form.control}
                  name="phone"
                  type="tel"
                  label={t('partner.newLead.fields.phone')}
                />
                <FormInput
                  control={form.control}
                  name="company"
                  label={t('partner.newLead.fields.company')}
                  placeholder={t('partner.newLead.placeholders.company')}
                  required
                />
                <FormInput
                  control={form.control}
                  name="role"
                  label={t('partner.newLead.fields.role')}
                  placeholder={t('partner.newLead.placeholders.role')}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <FieldSeparator>{t('partner.newLead.sections.qualification')}</FieldSeparator>
              <FormSelect
                control={form.control}
                name="interestProof"
                label={t('partner.newLead.fields.interestProof')}
                placeholder={t('partner.newLead.placeholders.select')}
                options={toOptions(
                  PartnerLeadInterestProofSchema.options,
                  INTEREST_PROOF_LABEL_KEYS
                )}
                required
              />
              {interestProof && (
                <FormInput
                  control={form.control}
                  name="contactDate"
                  type="date"
                  label={t('partner.newLead.fields.contactDate')}
                  required
                />
              )}
              {interestProof && contactDate && (
                <FormSelect
                  control={form.control}
                  name="positiveReaction"
                  label={t('partner.newLead.fields.positiveReaction')}
                  placeholder={t('partner.newLead.placeholders.select')}
                  options={toOptions(PartnerLeadReactionSchema.options, REACTION_LABEL_KEYS)}
                  required
                />
              )}
              {isDisqualified(positiveReaction) && (
                <p className="text-sm text-destructive">
                  {t('partner.newLead.notQualifiedNotice')}
                </p>
              )}
              {positiveReaction === REACTION_POSITIVE && (
                <>
                  <FormSelect
                    control={form.control}
                    name="licenseType"
                    label={t('partner.newLead.fields.licenseType')}
                    placeholder={t('partner.newLead.placeholders.select')}
                    options={toOptions(
                      PartnerLeadLicenseTypeSchema.options,
                      LICENSE_TYPE_LABEL_KEYS
                    )}
                    required
                  />
                  {wantsPartnerLicense(licenseType) && (
                    <FormInput
                      control={form.control}
                      name="typicalIndustries"
                      label={t('partner.newLead.fields.typicalIndustries')}
                    />
                  )}
                  <FormTextarea
                    control={form.control}
                    name="openQuestions"
                    label={t('partner.newLead.fields.openQuestions')}
                    placeholder={t('partner.newLead.placeholders.openQuestions')}
                    rows={3}
                  />
                </>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <FieldSeparator>{t('partner.newLead.sections.details')}</FieldSeparator>
              <FormTextarea
                control={form.control}
                name="comment"
                label={t('partner.newLead.fields.comment')}
                placeholder={t('partner.newLead.placeholders.comment')}
                rows={3}
              />
            </div>
          </div>
          {emailBlocked && (
            <p className="shrink-0 text-sm text-destructive">{t('partner.newLead.emailExists')}</p>
          )}
          <div className="flex shrink-0 justify-end gap-3 border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={goToAppointment} loading={precheck.isPending}>
              {t('partner.newLead.next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </>
      )}

      {/* Step 2 — book the appointment. The widget auto-resizes (resize: true)
            to its content, so there is no inner scrollbar; the dialog is the only
            scroll region. */}
      {step === 2 && (
        <>
          <p className="text-sm text-muted-foreground">{t('partner.newLead.appointmentHint')}</p>
          <CalendlyInline
            url={buildPartnerLeadCalendlyUrl(i18n.language)}
            className="min-h-[700px] w-full min-w-[320px]"
            prefill={{
              firstName,
              lastName,
              email,
              guests: me?.email ? [me.email] : undefined
            }}
            utm={me?.hubspotContactId ? { salesforceUuid: me.hubspotContactId } : undefined}
            onScheduled={handleScheduled}
          />
          {booked && (
            <p className="flex items-center gap-2 text-sm text-positive">
              <CalendarCheck className="w-4 h-4" />
              {t('partner.newLead.appointmentBooked')}
            </p>
          )}
          {hasAppointmentError && (
            <p className="text-sm text-destructive">{t('partner.newLead.appointmentRequired')}</p>
          )}
          <div className="flex shrink-0 justify-between gap-3 border-t pt-4">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.Back')}
            </Button>
            <Button
              onClick={form.handleSubmit(onSubmit)}
              disabled={!booked || isSubmitting}
              loading={isSubmitting}
            >
              {t('partner.newLead.submit')}
            </Button>
          </div>
        </>
      )}
    </>
  );
}
